import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import TopBar from "@/components/TopBar";
import BackButton from "@/components/BackButton";
import api, { streamUrl } from "@/lib/api";
import { catLabel, PLAN_META } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { ShieldCheck, Instagram, Youtube, Facebook, Linkedin, Twitter, AtSign, Camera, Globe, Phone, MapPin, Award, Settings, Sparkles, Share2, Pin, PinOff, BarChart3, FileText, Play, MessageSquare, Coins, Lock, Shield, Video, Film, Send, Download, Users } from "lucide-react";
import { toast } from "sonner";
import CastingInquiryModal from "@/components/CastingInquiryModal";
import SponsoredBanner from "@/components/SponsoredBanner";

const SOCIAL_ICONS = {
  instagram: Instagram, youtube: Youtube, facebook: Facebook,
  twitter: Twitter, linkedin: Linkedin, threads: AtSign,
  snapchat: Camera, portfolio_url: Globe,
};
const SOCIAL_LABELS = {
  instagram: "Instagram", youtube: "YouTube", facebook: "Facebook",
  twitter: "X", linkedin: "LinkedIn", threads: "Threads",
  snapchat: "Snapchat", portfolio_url: "Website",
};

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function Profile() {
  const { username } = useParams();
  const { user: me, refresh } = useAuth();
  const [user, setUser] = useState(null);
  const [videos, setVideos] = useState([]);
  const [completion, setCompletion] = useState(null);
  const [error, setError] = useState("");
  const [inquiryOpen, setInquiryOpen] = useState(false);

  const load = async () => {
    const uname = username || me?.username;
    if (!uname) return;
    try {
      const [u, v] = await Promise.all([
        api.get(`/users/${uname}`),
        api.get(`/videos/user/${uname}`),
      ]);
      setUser(u.data);
      setVideos(v.data.videos || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Not found");
    }
  };
  useEffect(() => { load(); }, [username, me]);
  useEffect(() => {
    if (!username || username === me?.username) {
      api.get("/users/me/completion").then(r => setCompletion(r.data)).catch(() => {});
    }
  }, [username, me]);

  if (error) return <div className="min-h-screen"><TopBar/><div className="max-w-2xl mx-auto px-4 pt-10 text-zinc-300">{error}</div></div>;
  if (!user) return <div className="min-h-screen"><TopBar/><div className="text-zinc-400 p-6 text-sm">Loading profile…</div></div>;

  const isMe = me?.user_id === user.user_id;
  const p = user.profile || {};
  const pinnedIds = p.pinned_video_ids || [];
  const pinned = pinnedIds.map(id => videos.find(v => v.id === id)).filter(Boolean);
  const introVideo = videos.find(v => v.id === p.intro_video_id);
  const restVideos = videos.filter(v => !pinnedIds.includes(v.id) && v.kind !== "audio" && v.id !== p.intro_video_id);
  const audios = videos.filter(v => v.kind === "audio");
  const photos = p.photos || [];
  const portfolioPdfs = p.portfolio_pdfs || [];
  const trailer = p.trailer;
  const isPublicVisit = !me;

  const shareProfile = async () => {
    const url = `${window.location.origin}/${user.username}`;
    if (navigator.share) {
      try { await navigator.share({ title: `${user.name} on NextScene`, url, text: `Check out ${user.name} — ${catLabel(user.category)} on NextScene.` }); return; } catch {}
    }
    await navigator.clipboard.writeText(url);
    toast.success("Profile link copied");
  };

  const togglePin = async (vid) => {
    try {
      const { data } = await api.post(`/users/me/pin/${vid}`);
      toast.success(pinnedIds.includes(vid) ? "Unpinned" : "Pinned");
      await load();
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Pin failed");
    }
  };

  return (
    <div data-testid="profile-page" className="min-h-screen pb-24">
      <TopBar subtitle={isMe ? "Profile" : `@${user.username}`}/>
      <div className="max-w-2xl mx-auto px-4 pt-4 space-y-4">
        {!isPublicVisit && <BackButton fallback={isMe ? "/app" : "/app/discover"} label="Back"/>}
        {isPublicVisit && (
          <div data-testid="public-cta" className="rounded-2xl border border-[#DC2626]/30 bg-gradient-to-r from-[#DC2626]/10 to-transparent p-3 flex items-center justify-between gap-3">
            <p className="text-xs text-zinc-200 leading-relaxed">Discover more India-Bharat talent on <span className="text-[#DC2626] font-semibold">NextScene</span></p>
            <Link to="/signup" data-testid="public-signup-cta" className="text-xs px-3 py-1.5 rounded-full bg-[#DC2626] hover:bg-[#EF4444] text-white font-semibold whitespace-nowrap">Join free</Link>
          </div>
        )}

        {/* Header */}
        <div className="rounded-2xl border border-zinc-800 bg-gradient-to-br from-[#180D0D] to-[#0A0605] p-5">
          <div className="flex items-start gap-4">
            <div className="w-20 h-20 rounded-full bg-[#DC2626]/15 border border-[#DC2626]/40 flex items-center justify-center text-[#FCA5A5] text-3xl font-bold shrink-0">
              {(user.name || "?").slice(0,1).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 data-testid="profile-name" className="font-display text-2xl font-bold truncate text-white">{user.name}</h1>
                {user.is_verified && <ShieldCheck size={16} className="text-[#F5C462]"/>}
              </div>
              <p className="text-sm text-zinc-400">@{user.username}</p>
              {user.category && <p className="text-sm text-[#DC2626] mt-1">{catLabel(user.category)}</p>}
              <div className="mt-3 flex gap-2 flex-wrap">
                {isMe ? (
                  <>
                    <Link to="/app/edit-profile" data-testid="edit-profile-btn" className="text-xs rounded-full border border-zinc-700 bg-zinc-900 px-3 py-1.5 hover:border-[#DC2626]/40 inline-flex items-center gap-1 text-white">
                      <Settings size={12}/> Edit
                    </Link>
                    <Link to="/app/settings" data-testid="settings-btn" className="text-xs rounded-full border border-zinc-700 bg-zinc-900 px-3 py-1.5 hover:border-[#F5C462]/40 inline-flex items-center gap-1 text-white">
                      <Settings size={12}/> Settings
                    </Link>
                    <Link to="/app/analytics" data-testid="analytics-btn" className="text-xs rounded-full border border-zinc-700 bg-zinc-900 px-3 py-1.5 hover:border-[#DC2626]/40 inline-flex items-center gap-1 text-white">
                      <BarChart3 size={12}/> Analytics
                    </Link>
                    <Link to="/app/rewards" data-testid="rewards-btn" className="text-xs rounded-full border border-[#F5C462]/50 bg-[#F5C462]/10 px-3 py-1.5 hover:border-[#F5C462] inline-flex items-center gap-1 text-[#F5C462] font-semibold">
                      <Coins size={12}/> {user.coins || 0} coins
                    </Link>
                    <Link to="/app/edit-profile#security" data-testid="security-btn" className="text-xs rounded-full border border-zinc-700 bg-zinc-900 px-3 py-1.5 hover:border-[#DC2626]/40 inline-flex items-center gap-1 text-white">
                      <Lock size={12}/> Password
                    </Link>
                    <Link to="/app/plans" data-testid="profile-plan-btn" className={`text-xs rounded-full px-3 py-1.5 border ${PLAN_META[user.plan].color} border-current`}>
                      {PLAN_META[user.plan].name} plan
                    </Link>
                    {user.is_admin && (
                      <Link to="/app/admin" data-testid="admin-panel-btn" className="text-xs rounded-full bg-gradient-to-r from-[#DC2626] to-[#7F1D1D] text-white px-3 py-1.5 font-semibold inline-flex items-center gap-1 shadow-lg shadow-[#DC2626]/30 border border-[#DC2626]/50">
                        <Shield size={12}/> Admin Panel
                      </Link>
                    )}
                    {user.is_verified ? (
                      <Link to="/app/story-card" data-testid="story-card-btn" className="text-xs rounded-full bg-[#DC2626] text-white px-3 py-1.5 font-semibold inline-flex items-center gap-1">
                        <Sparkles size={12}/> Story card
                      </Link>
                    ) : (
                      <Link to="/app/verification" data-testid="get-verified-btn" className="text-xs rounded-full bg-[#DC2626] text-white px-3 py-1.5 font-semibold inline-flex items-center gap-1">
                        <ShieldCheck size={12}/> Get verified
                      </Link>
                    )}
                  </>
                ) : null}
                <button data-testid="share-profile-btn" onClick={shareProfile} className="text-xs rounded-full border border-zinc-700 bg-zinc-900 px-3 py-1.5 hover:border-[#DC2626]/40 inline-flex items-center gap-1 text-white">
                  <Share2 size={12}/> Share
                </button>
                {!isMe && (
                  <button data-testid="cast-talent-btn" onClick={() => setInquiryOpen(true)} className="text-xs rounded-full bg-[#DC2626] hover:bg-[#EF4444] text-white px-3 py-1.5 font-semibold inline-flex items-center gap-1">
                    <MessageSquare size={12}/> Cast this talent
                  </button>
                )}
              </div>
            </div>
          </div>
          {p.bio && <p className="mt-4 text-sm text-zinc-200 leading-relaxed">{p.bio}</p>}
        </div>

        <FollowersSection username={user.username} followersCount={user.followers_count || 0}/>

        <CastingInquiryModal open={inquiryOpen} target={{ username: user.username }} onClose={() => setInquiryOpen(false)}/>

        <SponsoredBanner placement="profile" category={user.category || null}/>

        {/* Completion meter (own profile only, when < 100) */}
        {isMe && completion && completion.percent < 100 && (
          <div data-testid="completion-meter" className="rounded-2xl border border-[#DC2626]/30 bg-[#DC2626]/5 p-5">
            <div className="flex items-center justify-between mb-2">
              <p className="font-display text-lg font-semibold text-white">Profile is <span className="gold-text">{completion.percent}% complete</span></p>
              <span className="text-xs text-[#FCA5A5]">{100 - completion.percent}% to go</span>
            </div>
            <div className="h-2 rounded-full bg-zinc-800 overflow-hidden">
              <div className="h-full bg-[#DC2626] transition-all duration-500" style={{ width: `${completion.percent}%` }}/>
            </div>
            {completion.missing?.length > 0 && (
              <div className="mt-3 space-y-1">
                {completion.missing.slice(0, 4).map((m) => (
                  <Link key={m.label} to="/app/edit-profile" data-testid={`completion-${m.label.toLowerCase().replace(/\s/g,'-')}`} className="flex items-center justify-between text-sm text-zinc-200 hover:text-white py-1">
                    <span>+ {m.label}</span>
                    <span className="text-xs text-[#DC2626]">+{m.weight}%</span>
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Introduction video */}
        {introVideo && (
          <Section title="Introduction" icon={Play}>
            <video data-testid="intro-video" src={streamUrl(introVideo.storage_path)} controls playsInline className="w-full rounded-xl bg-black max-h-[50vh] object-contain"/>
          </Section>
        )}

        {/* Trailer reel */}
        {trailer?.path && (
          <Section title={`Trailer reel (${trailer.duration || 30}s)`} icon={Film}>
            <video data-testid="trailer-video" src={`${BACKEND}/api/users/trailer/${trailer.path}`} controls playsInline className="w-full rounded-xl bg-black max-h-[70vh] object-contain aspect-[9/16] mx-auto"/>
            <p className="text-[11px] text-zinc-500 mt-2">Auto-stitched from top performances · Generated {new Date(trailer.generated_at).toLocaleDateString()}</p>
          </Section>
        )}

        {/* Pinned Performances */}
        {pinned.length > 0 && (
          <Section title="Featured performances" icon={Pin}>
            <div className="grid grid-cols-3 gap-2">
              {pinned.map((v) => (
                <Link key={v.id} to={`/v/${v.id}`} data-testid={`pinned-${v.id}`} className="aspect-[9/16] rounded-xl overflow-hidden bg-black border border-[#DC2626]/50 relative">
                  <video src={streamUrl(v.storage_path)} muted playsInline preload="metadata" className="w-full h-full object-cover"/>
                  <div className="absolute top-1 left-1 text-[#DC2626] bg-black/60 rounded-full p-0.5"><Pin size={10}/></div>
                  {isMe && (
                    <button data-testid={`unpin-${v.id}`} onClick={(e) => { e.preventDefault(); togglePin(v.id); }} className="absolute top-1 right-1 bg-black/60 rounded-full p-1 text-white">
                      <PinOff size={10}/>
                    </button>
                  )}
                </Link>
              ))}
            </div>
          </Section>
        )}

        {/* Bento facts */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {p.city && <MetaCell label="City" icon={MapPin}>{p.city}{p.state ? `, ${p.state}` : ""}</MetaCell>}
          {(p.languages || []).length > 0 && <MetaCell label="Languages" span={2}>{p.languages.join(" · ")}</MetaCell>}
          {p.experience && <MetaCell label="Experience" span={2}>{p.experience}</MetaCell>}
          {(p.skills || []).length > 0 && <MetaCell label="Skills" span={4}>{p.skills.join(" · ")}</MetaCell>}
          {p.awards && <MetaCell label="Awards" icon={Award} span={2}>{p.awards}</MetaCell>}
          {p.education && <MetaCell label="Education" span={2}>{p.education}</MetaCell>}
          {p.availability && <MetaCell label="Availability" span={2}>{Array.isArray(p.availability) ? p.availability.join(" · ") : p.availability}</MetaCell>}
          {isMe && p.phone && <MetaCell label="Phone" icon={Phone} span={2}>{p.phone}</MetaCell>}
        </div>

        {/* Physical details */}
        {(p.height_cm || p.weight_kg || p.gender) && (
          <Section title="Physical details">
            <div className="grid grid-cols-3 gap-3 text-sm">
              {p.height_cm && <MetaCell label="Height">{p.height_cm} cm</MetaCell>}
              {p.weight_kg && <MetaCell label="Weight">{p.weight_kg} kg</MetaCell>}
              {p.gender && <MetaCell label="Gender">{p.gender}</MetaCell>}
            </div>
          </Section>
        )}

        {/* Photos gallery (typed portfolio) */}
        {(p.gallery_photos && p.gallery_photos.length > 0) ? (
          <Section title="Portfolio Gallery">
            <GalleryCarousel photos={p.gallery_photos}/>
          </Section>
        ) : photos.length > 0 && (
          <Section title="Portfolio">
            <div className="grid grid-cols-3 gap-2">
              {photos.map((path) => (
                <a key={path} href={`${BACKEND}/api/users/photo/${path}`} target="_blank" rel="noreferrer" data-testid={`photo-${path.slice(-10)}`} className="aspect-square rounded-lg overflow-hidden bg-black border border-zinc-800">
                  <img src={`${BACKEND}/api/users/photo/${path}`} alt="portfolio" className="w-full h-full object-cover"/>
                </a>
              ))}
            </div>
          </Section>
        )}

        {/* Resume */}
        {p.resume_url && (
          <Section title="Resume">
            <a href={`${BACKEND}/api/users/resume/${p.resume_url}`} target="_blank" rel="noreferrer" data-testid="resume-link" className="flex items-center gap-3 rounded-xl border border-zinc-800 bg-zinc-900 p-3 hover:border-[#DC2626]/40 transition-colors">
              <FileText size={18} className="text-[#DC2626]"/>
              <span className="text-sm text-white">Download resume (PDF)</span>
            </a>
          </Section>
        )}

        {/* Portfolio PDFs (decks, certificates, credits sheet) */}
        {portfolioPdfs.length > 0 && (
          <Section title="Portfolio documents">
            <div className="space-y-2">
              {portfolioPdfs.map((doc) => (
                <a key={doc.id} data-testid={`pdf-${doc.id}`} href={`${BACKEND}/api/users/portfolio-pdf/${doc.path}`} target="_blank" rel="noreferrer" className="flex items-center gap-3 rounded-xl border border-zinc-800 bg-zinc-900 p-3 hover:border-[#DC2626]/40 transition-colors">
                  <FileText size={18} className="text-[#DC2626] shrink-0"/>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate">{doc.title || "Document"}</p>
                    <p className="text-[11px] text-zinc-400">{Math.round((doc.size || 0)/1024)} KB · PDF</p>
                  </div>
                  <Download size={14} className="text-zinc-400 shrink-0"/>
                </a>
              ))}
            </div>
          </Section>
        )}

        {/* Socials */}
        {(() => {
          const links = Object.keys(SOCIAL_ICONS).filter(k => p[k]);
          if (!links.length) return null;
          return (
            <Section title="Social & portfolio">
              <div className="grid grid-cols-2 gap-2">
                {links.map((k) => {
                  const Icon = SOCIAL_ICONS[k];
                  return (
                    <a key={k} href={socialHref(k, p[k])} target="_blank" rel="noreferrer" className="flex items-center gap-2 rounded-xl border border-zinc-800 bg-zinc-900 p-3 hover:border-[#DC2626]/40 transition-colors text-sm text-white truncate">
                      <Icon size={14} className="text-[#DC2626] shrink-0"/> <span className="truncate">{SOCIAL_LABELS[k]}</span>
                    </a>
                  );
                })}
              </div>
            </Section>
          );
        })()}

        {/* Voice/audio clips */}
        {audios.length > 0 && (
          <Section title="Voice clips">
            <div className="space-y-2">
              {audios.map((a) => (
                <div key={a.id} data-testid={`audio-${a.id}`} className="rounded-xl border border-zinc-800 bg-[#180D0D] p-3">
                  <p className="text-sm text-white mb-2">{a.title || "Voice clip"} · {a.duration}s</p>
                  <audio src={streamUrl(a.storage_path)} controls className="w-full"/>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* All videos */}
        <div>
          <h2 className="font-display text-lg font-semibold mb-3 text-white">Performances ({restVideos.length})</h2>
          {restVideos.length === 0 ? (
            <p className="text-sm text-zinc-400">No performances yet.</p>
          ) : (
            <div className="grid grid-cols-3 gap-2">
              {restVideos.map((v) => (
                <div key={v.id} className="relative">
                  <Link to={`/v/${v.id}`} data-testid={`profile-video-${v.id}`} className="block aspect-[9/16] rounded-xl overflow-hidden bg-black border border-zinc-800">
                    <video src={streamUrl(v.storage_path)} muted playsInline preload="metadata" className="w-full h-full object-cover"/>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent pointer-events-none"/>
                    <div className="absolute bottom-1 left-1 right-1 text-xs text-white line-clamp-1">{v.title || "Untitled"}</div>
                  </Link>
                  {isMe && (
                    <button data-testid={`pin-${v.id}`} onClick={() => togglePin(v.id)} className="absolute top-1 right-1 bg-black/70 border border-zinc-700 rounded-full p-1.5 text-white hover:text-[#DC2626] hover:border-[#DC2626]/50">
                      <Pin size={12}/>
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function socialHref(key, val) {
  if (!val) return "#";
  if (val.startsWith("http")) return val;
  const handle = val.replace(/^@/, "");
  const map = {
    instagram: `https://instagram.com/${handle}`,
    youtube: `https://youtube.com/@${handle}`,
    facebook: `https://facebook.com/${handle}`,
    twitter: `https://x.com/${handle}`,
    linkedin: `https://linkedin.com/in/${handle}`,
    threads: `https://threads.net/@${handle}`,
    snapchat: `https://snapchat.com/add/${handle}`,
    portfolio_url: val.startsWith("http") ? val : `https://${val}`,
  };
  return map[key] || val;
}

function FollowersSection({ username, followersCount }) {
  const [tab, setTab] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [followingCount, setFollowingCount] = useState(null);
  const [query, setQuery] = useState("");
  useEffect(() => { api.get(`/users/${username}/following?limit=1`).then(r => setFollowingCount(r.data.count)).catch(() => {}); }, [username]);
  const open = async (which) => {
    if (tab === which) { setTab(null); setRows([]); setQuery(""); return; }
    setTab(which); setLoading(true); setQuery("");
    try { const { data } = await api.get(`/users/${username}/${which}?limit=100`); setRows(data.users || []); }
    catch { setRows([]); }
    finally { setLoading(false); }
  };
  const filtered = query.trim()
    ? rows.filter(u => {
        const s = query.toLowerCase();
        return (u.name || "").toLowerCase().includes(s) || (u.username || "").toLowerCase().includes(s) || (u.category || "").toLowerCase().includes(s);
      })
    : rows;
  return (
    <div data-testid="followers-section" className="space-y-2">
      <div className="flex gap-2">
        <button data-testid="tab-followers" onClick={() => open("followers")} className={`flex-1 rounded-2xl border p-3 text-left transition-colors ${tab === "followers" ? "border-[#DC2626] bg-[#DC2626]/10" : "border-zinc-800 bg-[#180D0D] hover:border-[#DC2626]/40"}`}>
          <p className="text-[10px] uppercase tracking-wider text-zinc-400 flex items-center gap-1"><Users size={11}/> Followers</p>
          <p className="text-lg font-display font-bold text-white mt-0.5">{(followersCount ?? 0).toLocaleString("en-IN")}</p>
        </button>
        <button data-testid="tab-following" onClick={() => open("following")} className={`flex-1 rounded-2xl border p-3 text-left transition-colors ${tab === "following" ? "border-[#DC2626] bg-[#DC2626]/10" : "border-zinc-800 bg-[#180D0D] hover:border-[#DC2626]/40"}`}>
          <p className="text-[10px] uppercase tracking-wider text-zinc-400 flex items-center gap-1"><Users size={11}/> Following</p>
          <p className="text-lg font-display font-bold text-white mt-0.5">{followingCount == null ? "—" : (followingCount ?? 0).toLocaleString("en-IN")}</p>
        </button>
      </div>
      {tab && (
        <div data-testid={`${tab}-list`} className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-3 space-y-2">
          <input data-testid="followers-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={`Search ${tab}… (name, @handle, category)`} className="w-full bg-zinc-900 border border-zinc-800 rounded-full px-4 h-9 text-sm text-white focus:outline-none focus:border-[#DC2626]/50"/>
          {loading ? <p className="text-sm text-zinc-500">Loading…</p> : filtered.length === 0 ? <p className="text-sm text-zinc-500">{rows.length === 0 ? `No ${tab} yet.` : "No match."}</p> : (
            <ul className="space-y-2 max-h-96 overflow-y-auto">
              {filtered.map((u) => (
                <li key={u.user_id} data-testid={`${tab}-row-${u.username}`}>
                  <Link to={`/${u.username}`} className="flex items-center gap-3 rounded-xl hover:bg-zinc-900/60 p-2 transition-colors">
                    <div className="w-9 h-9 rounded-full bg-[#DC2626]/15 border border-[#DC2626]/40 flex items-center justify-center text-[#FCA5A5] text-sm font-bold shrink-0">{(u.name || "?").slice(0,1).toUpperCase()}</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-white truncate flex items-center gap-1">{u.name} {u.is_verified && <ShieldCheck size={11} className="text-[#F5C462]"/>}</p>
                      <p className="text-xs text-zinc-400 truncate">@{u.username} · {catLabel(u.category)}</p>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
          <p className="text-[10px] text-zinc-500">{filtered.length} of {rows.length}</p>
        </div>
      )}
    </div>
  );
}


function Section({ title, icon: Icon, children }) {
  return (
    <div className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-5">
      <div className="flex items-center gap-2 mb-3">
        {Icon && <Icon size={16} className="text-[#DC2626]"/>}
        <h2 className="font-display text-lg font-semibold text-white">{title}</h2>
      </div>
      {children}
    </div>
  );
}

function MetaCell({ label, icon: Icon, children, span = 1 }) {
  const colSpan = span === 4 ? "col-span-2 md:col-span-4" : span === 2 ? "col-span-2 md:col-span-2" : "col-span-1";
  return (
    <div className={`rounded-xl border border-zinc-800 bg-[#180D0D] p-3 ${colSpan}`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-zinc-400">
        {Icon && <Icon size={11}/>} {label}
      </div>
      <div className="mt-1 text-sm text-zinc-100 leading-snug">{children}</div>
    </div>
  );
}

function GalleryCarousel({ photos }) {
  const [active, setActive] = useState(0);
  const [types, setTypes] = useState([]);
  useEffect(() => { api.get("/gallery/photo-types").then(r => setTypes(r.data.types || [])).catch(() => {}); }, []);
  const typeLabel = (k) => (types.find(t => t.key === k) || { label: k }).label;
  if (!photos || photos.length === 0) return null;
  const current = photos[active];
  return (
    <div data-testid="gallery-carousel" className="space-y-3">
      <div className="relative rounded-2xl overflow-hidden border border-zinc-800 bg-black aspect-[3/4] max-h-[560px]">
        <img src={`${BACKEND}/api/users/photo/${current.path}`} alt={typeLabel(current.photo_type)} className="w-full h-full object-cover"/>
        <span data-testid={`carousel-badge-${current.id}`} className="absolute top-3 left-3 rounded-full bg-black/70 backdrop-blur border border-[#F5C462]/40 text-[#F5C462] text-[11px] font-semibold px-3 py-1">
          {typeLabel(current.photo_type)}
        </span>
        {photos.length > 1 && (
          <>
            <button data-testid="carousel-prev" onClick={() => setActive((active - 1 + photos.length) % photos.length)} className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/60 border border-zinc-700 text-white flex items-center justify-center backdrop-blur hover:bg-black/80 transition-colors" aria-label="Previous">‹</button>
            <button data-testid="carousel-next" onClick={() => setActive((active + 1) % photos.length)} className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/60 border border-zinc-700 text-white flex items-center justify-center backdrop-blur hover:bg-black/80 transition-colors" aria-label="Next">›</button>
            <span className="absolute bottom-3 right-3 text-[11px] text-white bg-black/70 border border-zinc-700 rounded-full px-2 py-0.5 backdrop-blur">{active + 1} / {photos.length}</span>
          </>
        )}
      </div>
      {photos.length > 1 && (
        <div className="flex gap-1.5 overflow-x-auto scrollbar-hide pb-1">
          {photos.map((p, i) => (
            <button key={p.id} data-testid={`carousel-thumb-${p.id}`} onClick={() => setActive(i)} className={`relative shrink-0 w-14 h-16 rounded-lg overflow-hidden border-2 transition-all ${i === active ? "border-[#F5C462]" : "border-zinc-800 opacity-60 hover:opacity-100"}`}>
              <img src={`${BACKEND}/api/users/photo/${p.path}`} alt="" className="w-full h-full object-cover"/>
              <span className="absolute bottom-0 inset-x-0 bg-black/70 text-[7px] text-white text-center py-0.5 truncate leading-tight">{typeLabel(p.photo_type)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

